import Table from "../../../Components/Table/Table";
import SVG from "../../../Components/SVG";
import Custom from "../../../Components/Buttons/Custom";
import { toJalaliDateTime } from "../../../helper/toJalaliDateTime";
import TableLoading from "../../../Components/Loader/TableLoading";
import InfiniteScrollFooter from "../../../Components/InfiniteScrollFooter";
import { formatLeverage } from "../../../helper/leverage";
export default function TradeHistory({ accountData, accLoading, loadingMore, error, sentinelRef, scrollRootRef, onRetry, hasNext }) {
  const formatDate = (value) => {
    if (value == null || value === "") return "-";
    try {
      if (typeof value === "string" && Number.isNaN(Number(value))) {
        const date = new Date(value);
        return Number.isNaN(date.getTime())
          ? "-"
          : new Intl.DateTimeFormat("fa-IR", {
              timeZone: "Asia/Tehran",
              year: "numeric", month: "2-digit", day: "2-digit",
              hour: "2-digit", minute: "2-digit", second: "2-digit",
            }).format(date);
      }
      const numeric = Number(value);
      return toJalaliDateTime(numeric > 100000000000 ? numeric / 1000 : numeric);
    } catch {
      return "-";
    }
  };
  const formatPriceTrimmed = (value) => {
    const num = Number(value);
    return Number.isFinite(num)
      ? num.toLocaleString("en-US", { maximumFractionDigits: 18 })
      : "-";
  };

  const handleDownload = () => {
    if (!accountData?.trades?.length || typeof document === "undefined") {
      return;
    }

    const headers = [
      "کد معامله",
      "زمان باز شدن",
      "قیمت باز شدن",
      "زمان بسته شدن",
      "قیمت بسته شدن",
      "سود",
      "حجم",
      "اهرم",
      "نماد",
      "نوع معامله",
    ];

    const escapeCSV = (value) => {
      if (value === null || value === undefined) return "";
      const stringValue = String(value);
      return /[",\n]/.test(stringValue)
        ? `"${stringValue.replace(/"/g, '""')}"`
        : stringValue;
    };

    const formatMoney = (value) =>
      Number(Number(value).toFixed(2)).toLocaleString();

    const rows = accountData?.trades?.map((trade) => {
      const pnl = Number(trade.profit_loss);
      const formattedPnl =
        pnl >= 0 ? `$${formatMoney(pnl)}` : `-$${formatMoney(Math.abs(pnl))}`;

      return [
        trade.id,
        toJalaliDateTime(trade.opened_at),
        `$${formatPriceTrimmed(trade.entry_price)}`,
        toJalaliDateTime(trade.closed_at),
        `$${formatPriceTrimmed(trade.exit_price)}`,
        formattedPnl,
        `${formatMoney(trade.margin_usdt)} USDT`,
        formatLeverage(trade.leverage),
        trade.symbol?.toUpperCase?.() ?? "",
        trade.side === "long" ? "Buy" : "Sell",
      ]
        .map(escapeCSV)
        .join(",");
    });

    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `trade-history-${
      accountData?.accountCode ??
      accountData?.account?.id ??
      accountData?.id ??
      "export"
    }.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="w-full p-5 flex flex-col gap-5 rounded-2xl bg-zi-bg2-light dark:bg-zi-bg2-dark">
      <div className="flex justify-between items-center">
        <h3 className="flex gap-2.5 items-center">
          <SVG
            name="archive"
            classname="size-5 stroke-zi-text-light dark:stroke-zi-text-dark"
          />
          <p className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
            تاریخچه معاملات
          </p>
        </h3>
        <Custom
          onClick={handleDownload}
          text="دانلود CSV"
          className="bg-zi-gradient-button-blue !px-5 !py-2 !rounded-full text-zi-text-dark hocus:ring-1 hocus:ring-offset-1 hocus:ring-zi-border-dark"
        />
      </div>

      {accLoading ? (
        <Table
          isLTR={true}
          columns={[
            { key: "uuid", label: "کد معامله" },
            { key: "openTime", label: "زمان باز شدن" },
            { key: "openPrice", label: "قیمت باز شدن" },
            { key: "closeTime", label: "زمان بسته شدن" },
            { key: "closePrice", label: "قیمت بسته شدن" },
            { key: "pnl", label: "سود" },
            { key: "volume", label: "حجم" },
            { key: "leverage", label: "اهرم" },
            { key: "symbol", label: "نماد" },
            { key: "tradeType", label: "نوع معامله" },
          ]}
          data={[]}
        />
      ) : accountData?.trades?.length > 0 ? (
        <Table
          maxHeight="60dvh"
          haveScroll={true}
          isLTR={true}
          columns={[
            { key: "uuid", label: "کد معامله" },
            { key: "openTime", label: "زمان باز شدن" },
            { key: "openPrice", label: "قیمت باز شدن" },
            { key: "closeTime", label: "زمان بسته شدن" },
            { key: "closePrice", label: "قیمت بسته شدن" },
            { key: "pnl", label: "سود" },
            { key: "volume", label: "حجم" },
            { key: "leverage", label: "اهرم" },
            { key: "symbol", label: "نماد" },
            { key: "tradeType", label: "نوع معامله" },
          ]}
          data={accountData?.trades?.map((trade) => ({
            uuid: trade.id,
            openTime: formatDate(trade.opened_at),
            openPrice: `$${formatPriceTrimmed(trade.entry_price)}`,
            closeTime: formatDate(trade.closed_at),
            closePrice: `$${formatPriceTrimmed(trade.exit_price)}`,
            pnl:
              Number(trade.profit_loss) >= 0
                ? `$${Number(
                    Number(trade.profit_loss).toFixed(2),
                  ).toLocaleString()}`
                : `-$${Number(
                    Math.abs(Number(trade.profit_loss)).toFixed(2),
                  ).toLocaleString()}`,
            volume: `${Number(
              Number(trade.margin_usdt).toFixed(2),
            ).toLocaleString()} USDT`,
            leverage: formatLeverage(trade.leverage),
            symbol: trade.symbol?.toUpperCase?.() ?? "-",
            tradeType: trade.side === "long" ? "Buy" : "Sell",
            detail: "",
          }))}
          footer={<InfiniteScrollFooter sentinelRef={sentinelRef} loading={loadingMore} error={error} onRetry={onRetry} hasNext={hasNext} />}
          scrollRootRef={scrollRootRef}
        />
      ) : (
        <Table
          isLTR={true}
          columns={[
            { key: "uuid", label: "کد معامله" },
            { key: "openTime", label: "زمان باز شدن" },
            { key: "openPrice", label: "قیمت باز شدن" },
            { key: "closeTime", label: "زمان بسته شدن" },
            { key: "closePrice", label: "قیمت بسته شدن" },
            { key: "pnl", label: "سود" },
            { key: "volume", label: "حجم" },
            { key: "leverage", label: "اهرم" },
            { key: "symbol", label: "نماد" },
            { key: "tradeType", label: "نوع معامله" },
          ]}
          data={[]}
        />
      )}
      {accLoading && (
        <div className="w-full flex justify-center items-center gap-2">
          <p className="text-[#808080]">در حال بارگذاری</p>
          <TableLoading className="text-[#808080]" />
        </div>
      )}
      {!accLoading && !error && accountData?.trades && accountData?.trades?.length === 0 && (
        <div className="w-full flex justify-center items-center flex-col gap-5">
          <p className="text-zi-text-light dark:text-zi-text-dark">
            موردی برای نمایش وجود ندارد
          </p>
          <SVG name="noData" classname="w-24 fill-cyan-500" />
        </div>
      )}
      {!accLoading && accountData?.trades?.length === 0 && error && (
        <InfiniteScrollFooter sentinelRef={sentinelRef} loading={loadingMore} error={error} onRetry={onRetry} hasNext={hasNext} />
      )}
    </div>
  );
}
